// src/components/profile/NotificationSettings.tsx
// 推送通知設定組件

import React, { useState } from 'react';
import { useNotificationPermission } from '../../hooks/useNotificationPermission';
import { useAdminCheck } from '../../hooks/useAdminCheck';
import { sendTestPushNotification } from '../../services/api/pushNotifications';

/**
 * 推送通知設定組件
 * 讓用戶啟用/管理推送通知權限
 *
 * ⚠️ 僅 Admin 可見
 */
export const NotificationSettings: React.FC = () => {
  const {
    permission,
    isSupported,
    isLoading,
    requestPermission,
    fcmToken
  } = useNotificationPermission();

  const { isAdmin, loading: adminLoading } = useAdminCheck();

  const [isSendingTest, setIsSendingTest] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);

  const handleEnableNotifications = async () => {
    // requestPermission 會自動處理 Token 儲存
    await requestPermission();
  };

  const handleSendTest = async () => {
    setIsSendingTest(true);
    setTestResult(null);

    try {
      const result = await sendTestPushNotification();

      if (result.success) {
        setTestResult('✅ 測試推播已發送！請檢查通知。');
      } else {
        setTestResult(`❌ 發送失敗：${result.error}`);
      }
    } catch (error: any) {
      setTestResult(`❌ 發送失敗：${error.message}`);
    } finally {
      setIsSendingTest(false);

      // 3 秒後自動清除結果訊息
      setTimeout(() => setTestResult(null), 3000);
    }
  };

  // ⚠️ 非 Admin 不顯示此組件
  if (adminLoading) {
    return null; // 檢查中不顯示
  }

  if (!isAdmin) {
    return null; // 非 Admin 不顯示
  }

  if (isLoading) {
    return (
      <div className="p-4 bg-white rounded-lg border border-gray-200">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (!isSupported) {
    return (
      <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
        <div className="flex items-start">
          <span className="text-2xl mr-3">⚠️</span>
          <div>
            <h3 className="font-semibold text-yellow-800 mb-1">
              Browser not supported
            </h3>
            <p className="text-sm text-yellow-700">
              Your browser doesn't support push notifications. Please use Chrome, Firefox, or Edge.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-white rounded-lg border border-gray-200">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 mb-1">
            Push Notifications
          </h3>
          <p className="text-sm text-gray-600">
            Receive reminders for tasks and important updates
          </p>
        </div>

        <div className="ml-4">
          {permission === 'default' && (
            <button
              onClick={handleEnableNotifications}
              className="px-4 py-2 btn-primary rounded-lg transition-colors font-medium"
            >
              Enable
            </button>
          )}

          {permission === 'granted' && (
            <div className="flex items-center text-green-600">
              <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="font-medium">Enabled</span>
            </div>
          )}

          {permission === 'denied' && (
            <div className="text-red-600">
              <p className="text-sm font-medium">Blocked</p>
              <p className="text-xs mt-1">
                Please enable in browser settings
              </p>
            </div>
          )}
        </div>
      </div>

      {permission === 'granted' && fcmToken && (
        <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
          {/* 測試推播按鈕 */}
          <div>
            <button
              onClick={handleSendTest}
              disabled={isSendingTest}
              className="w-full px-4 py-2 btn-secondary rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSendingTest ? 'Sending...' : '🧪 Send Test Notification'}
            </button>

            {testResult && (
              <p className="text-sm mt-2 text-center">
                {testResult}
              </p>
            )}
          </div>

          {/* Token 資訊 */}
          <div>
            <p className="text-xs text-gray-500">
              Device registered for notifications
            </p>
            <p className="text-xs text-gray-400 mt-1 font-mono truncate">
              {fcmToken.substring(0, 40)}...
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
